// styles
import {buttonGreen, buttonRed, grey, white} from '../../theme/vars';
import TouchableOpacity from '../../containers/touchableOpacity/TouchableOpacity';
import styled from '@emotion/styled';
import {motion} from 'framer-motion';

type ContainerProps = {
  percent?: number;
};

const Container = styled(TouchableOpacity)<ContainerProps>`
  display: flex;
  height: 3rem;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 0;

  .buttonOutline {
    border: 2px solid white;
    border-radius: 8px;
    height: 100%;
    width: 100%;
  }

  .disabledButtonOutline {
    border-color: ${grey};
  }
`;

const ButtonFill = styled(motion.div)<ContainerProps>`
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: ${props => props.percent}%;
  border-bottom-right-radius: ${props => (props.percent === 100 ? '8px' : '0')};
  border-top-right-radius: ${props => (props.percent === 100 ? '8px' : '0')};
`;

const TriviaText = styled(motion.div)<ContainerProps>`
  color: ${white};
  font-family: 'Roboto-Bold';
  font-size: 15px;
  left: 20px;
  line-height: 15px;
  position: absolute;
  text-transform: uppercase;

  &.disabledText {
    color: ${grey};
  }
`;

const PercentText = styled(motion.div)`
  color: ${white};
  font-family: 'Roboto-Regular';
  font-size: 14px;
  right: 20px;
  line-height: 15px;
  position: absolute;
`;

const pageTransition = (delay: number | undefined) => {
  return {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.7,
    delay,
  };
};

const buttonVariants = {
  initial: ({
    backgroundColor,
    percent,
    shouldAnimateWidth,
    shouldAnimateBackgroundColor,
    isAnswer,
    isNotActive,
  }: {
    backgroundColor: string | undefined;
    percent: number;
    shouldAnimateWidth: boolean;
    shouldAnimateBackgroundColor: boolean;
    isAnswer: boolean;
    isNotActive: boolean;
  }) => {
    return {
      width: `${shouldAnimateWidth ? 0 : percent}%`,
      backgroundColor: shouldAnimateBackgroundColor
        ? isAnswer
          ? isNotActive
            ? 'rgba(0, 0, 0, 0)'
            : 'rgb(50, 122, 194)'
          : 'rgb(50, 122, 194)'
        : backgroundColor,
    };
  },
  in: ({
    backgroundColor,
    percent,
    shouldAnimateWidth,
    shouldAnimateBackgroundColor,
    isAnswer,
  }: {
    backgroundColor: string | undefined;
    percent: number;
    shouldAnimateWidth: boolean;
    shouldAnimateBackgroundColor: boolean;
    isAnswer: boolean;
  }) => {
    return {
      ...(shouldAnimateWidth && {width: `${percent}%`}),
      backgroundColor: shouldAnimateBackgroundColor
        ? isAnswer
          ? buttonGreen
          : buttonRed
        : backgroundColor,
    };
  },
};

const textVariants = {
  initial: (animateTextColor: any) => ({
    ...(animateTextColor && {borderColor: 'rgb(255, 255, 255)'}),
  }),
  in: (animateTextColor: any) => ({
    ...(animateTextColor && {borderColor: 'rgb(119, 119, 119)'}),
  }),
};

type Props = {
  disabled: boolean;
  hasDisabledStyle?: boolean;
  backgroundColor: string;
  delay?: number;
  animateTextColor?: boolean;
  shouldAnimateBackgroundColor?: boolean;
  isAnswer?: boolean;
  option: any;
  onPressTriviaButton?: (id: number) => void;
  activeAnswer?: string | null;
  percent: number;
  shouldAnimateWidth: boolean;
  showPercent?: boolean;
};

const TriviaButton = ({
  disabled,
  hasDisabledStyle = false,
  backgroundColor,
  delay,
  animateTextColor,
  shouldAnimateBackgroundColor,
  isAnswer,
  option,
  onPressTriviaButton,
  activeAnswer,
  percent,
  shouldAnimateWidth,
  showPercent = false,
}: Props) => {
  const isNotActive = activeAnswer !== option.id;
  const transition = pageTransition(delay);

  return (
    <Container
      disabled={disabled}
      handleClick={() => {
        onPressTriviaButton?.(option.id);
      }}>
      <motion.div
        className={`buttonOutline${
          (hasDisabledStyle && ' disabledButtonOutline') || ''
        }`}
        custom={animateTextColor}
        initial="initial"
        animate="in"
        transition={transition}
        variants={textVariants}
      />
      <ButtonFill
        percent={percent}
        custom={{
          backgroundColor,
          percent,
          shouldAnimateWidth,
          shouldAnimateBackgroundColor,
          isAnswer,
          isNotActive,
        }}
        variants={buttonVariants}
        initial="initial"
        animate="in"
        transition={transition}
      />
      <TriviaText
        className={`${(hasDisabledStyle && 'disabledText') || ''}`}
        custom={animateTextColor}
        initial="initial"
        animate="in"
        transition={transition}
        variants={textVariants}>
        {option.answer}
      </TriviaText>
      {showPercent && percent >= 0 && (
        <PercentText
          custom={animateTextColor}
          initial="initial"
          animate="in"
          transition={transition}
          variants={textVariants}>
          {`${percent}%`}
        </PercentText>
      )}
    </Container>
  );
};

export default TriviaButton;
